// Centralized API Service

const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEO_API_URL = 'https://validationapi.thetechmenders.com/api';

/**
 * Fetch user's geolocation data
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(`${GEO_API_URL}/GeoLocation/GetGeoLocation`, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    const data = await response.json();
    
    if (data.isSuccess) {
      return {
        success: true,
        data: data.data,
        rawResponse: data
      };
    } else {
      return {
        success: false,
        error: data.errorMessage || 'Failed to fetch geolocation'
      };
    }
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      error: 'Network error while fetching geolocation'
    };
  }
};

/**
 * Submit lead to CRM
 * @param {Object} formData - Form data containing name, email, phone, etc.
 * @param {Object} geoData - Geolocation data (optional)
 * @param {Object} packageInfo - Package information (optional)
 */
export const submitLead = async (formData, geoData = null, packageInfo = null) => {
  try {
    // Extract only numeric phone number
    const phoneNumber = formData.phoneNumber.replace(/\D/g, '');
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryDialCode) || 1,
      country: formData.country || 'US',
      phoneNumber: phoneNumber,
      ipAddress: geoData?.ipAddress || '0.0.0.0',
      description: formData.description || formData.message || '',
      pageUrl: window.location.href,
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? JSON.stringify({ 
        isSuccess: true, 
        data: geoData, 
        errorMessage: '', 
        statusCode: 200 
      }) : '',
      packageTitle: packageInfo?.title || '',
      packageAmount: packageInfo?.amount || 0
    };

    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different status codes
    if (response.status === 200 && data.isSuccess) {
      return {
        success: true,
        customerId: data.data.customerId,
        clientId: data.data.clientId,
        message: data.data.message,
        redirectUrl: `/application/main?customerid=${data.data.customerId}`
      };
    } else if (response.status === 401) {
      return {
        success: false,
        statusCode: 401,
        error: data.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Extract error from errorMessage field (as per API spec)
      let errorMessage = data.errorMessage || data.data?.message || 'Invalid request';
      
      // Check for validation errors in data.errors object
      if (data.errors && typeof data.errors === 'object') {
        const firstErrorKey = Object.keys(data.errors)[0];
        if (firstErrorKey && Array.isArray(data.errors[firstErrorKey])) {
          errorMessage = data.errors[firstErrorKey][0];
        }
      }
      
      return {
        success: false,
        statusCode: 400,
        error: errorMessage
      };
    } else {
      return {
        success: false,
        statusCode: response.status,
        error: data.errorMessage || data.message || 'Something went wrong'
      };
    }
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Get country code from phone number input
 * Extracts country dial code from formatted phone number
 */
export const extractCountryDialCode = (phoneNumber) => {
  // Remove all non-numeric characters
  const numericOnly = phoneNumber.replace(/\D/g, '');
  
  // Common country codes (you can expand this)
  const countryCodes = {
    '1': 'US',
    '44': 'GB',
    '91': 'IN',
    '92': 'PK',
    '31': 'NL',
    '49': 'DE',
    '33': 'FR',
    '81': 'JP',
    '86': 'CN'
  };
  
  // Try to match country code
  for (let code in countryCodes) {
    if (numericOnly.startsWith(code)) {
      return {
        dialCode: parseInt(code),
        country: countryCodes[code],
        phoneNumber: numericOnly.substring(code.length)
      };
    }
  }
  
  // Default to US if no match
  return {
    dialCode: 1,
    country: 'US',
    phoneNumber: numericOnly
  };
};

/**
 * Format phone number for display (optional helper)
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return phoneNumber;
};
